import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { getAllFiles, uploadFile, deleteFileById } from '../utils/api/FileAPI';
import { convertToIndianTimeZone } from '../utils/utils';
import { toast } from 'sonner'; 

function KnowledgeBaseLayer() {
    const [files, setFiles] = useState([]);
    const [page, setPage] = useState(1);
    const [limit] = useState(10);
    const [totalRecords, setTotalRecords] = useState(0);
    const [loading, setLoading] = useState(true);
    const [lastKey, setLastKey] = useState('');
    const [fileNames, setFileNames] = useState([]);
    const [isDragOver, setIsDragOver] = useState(false);

    // NEW: keep track of files being uploaded (UX fix)
    const [uploading, setUploading] = useState([]); // [{ name, bytes, mimetype }]

    const totalPages = useMemo(() => {
        const total = Math.ceil(totalRecords / limit);
        return total > 0 ? total : 1;
    }, [totalRecords, limit]);

    const fetchData = useCallback(async () => {
        const key = `${page}_${limit}`;
        if (key === lastKey && files.length > 0) return;

        try {
            setLoading(true);
            const res = await getAllFiles({ page, limit });
            setFiles(res?.files || []);
            setTotalRecords(res?.total || 0);
            setLastKey(key);
        } catch (err) {
            console.error("Failed to fetch files", err);
            toast.error('Failed to load files'); 
        } finally {
            setLoading(false);
        }
    }, [page, limit, lastKey, files.length]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleFileChange = async (event) => {
        const selectedFiles = Array.from(event.target.files || []);
        await handleFiles(selectedFiles);
        if (event?.target) event.target.value = ''; // allow re-selecting same file
    };

    // Centralized validation + upload + toasts + "Uploading..." rows
    const handleFiles = async (selectedFiles) => {
        if (!selectedFiles?.length) return;

        const allowedExt = ['pdf', 'doc', 'docx', 'txt'];

        // Partition by extension
        const valid = [];
        const rejected = [];
        for (const f of selectedFiles) {
            const ext = f.name.split('.').pop()?.toLowerCase();
            if (ext && allowedExt.includes(ext)) valid.push(f);
            else rejected.push(f.name);
        }

        if (rejected.length) {
            toast.error(`Only PDF, DOC, DOCX, TXT allowed. Rejected: ${rejected.join(', ')}`);
        }

        // Show pending rows immediately (UX)
        const pendingRows = valid.map((f) => ({
            name: f.name,
            bytes: f.size ?? 0,
            mimetype: f.type || 'application/octet-stream'
        }));
        setUploading((prev) => [...prev, ...pendingRows]);

        // Update local names list (avoid duplicates)
        const newFileNames = valid.map((f) => f.name);
        setFileNames((prev) => [...prev, ...newFileNames.filter(n => !prev.includes(n))]);

        // Upload valid files one-by-one with toasts
        for (const file of valid) {
            try {
                await uploadFile(file); // should throw on server error
                toast.success(`${file.name} uploaded successfully`);
                // Remove from uploading rows as soon as it completes
                setUploading((prev) => prev.filter((u) => u.name !== file.name));
            } catch (error) {
                const msg = (error && (error.message || error.error || String(error))) || 'Upload failed';
                toast.error(`${file.name}: ${msg}`);
                // Mark as failed in the uploading list (so user sees it)
                setUploading((prev) =>
                    prev.map((u) =>
                        u.name === file.name ? { ...u, _failed: true } : u
                    )
                );
            }
        }

        // Refresh server data
        setLastKey("");
        fetchData();
    };

    const handleDrop = async (event) => {
        event.preventDefault();
        setIsDragOver(false);
        const droppedFiles = Array.from(event.dataTransfer?.files || []);
        await handleFiles(droppedFiles);
    };

    const removeFileName = (name) => {
        setFileNames((prev) => prev.filter((fileName) => fileName !== name));
    };

    const handleDeleteFile = async (fileId) => {
        try {
            await deleteFileById(fileId);
            toast.success('File deleted');
            setLastKey("");
            fetchData();
        } catch (error) {
            console.error("Error deleting file:", error);
            toast.error('Failed to delete file');
        }
    };

    const getStatusStyle = (status) => {
        switch ((status || '').toLowerCase()) {
            case 'processing':
                return 'bg-warning-100 text-warning-600';
            case 'failed':
                return 'bg-danger-100 text-danger-600';
            case 'done':
                return 'bg-success-100 text-success-600';
            case 'uploading': // NEW visual
                return 'bg-primary-100 text-primary-600';
            default:
                return 'bg-neutral-100 text-secondary-light';
        }
    };

    return (
        <div className="col" style={{ height: 'auto' }}>
            <div
                className={`card overflow-hidden position-relative radius-12 ${isDragOver ? 'border border-primary-500 bg-primary-50' : ''}`}
                onDragOver={(e) => {
                    e.preventDefault();
                    setIsDragOver(true);
                }}
                onDragLeave={() => setIsDragOver(false)}
                onDrop={handleDrop}
            >
                <div className="border-bottom">
                    <div className="bg-base d-flex align-items-center flex-wrap " style={{ gap: '12px', padding: '16px 16px', justifyContent: 'flex-end' }}>
                        <div>
                            <label
                                htmlFor="file-upload-name"
                                className="mb-0 border border-neutral-600 fw-medium text-secondary-light px-16 py-10 radius-12 d-inline-flex align-items-center gap-2 bg-hover-neutral-200"
                                style={{ fontSize: '14px', cursor: 'pointer' }}
                                aria-busy={uploading.length > 0} // hint for assistive tech
                            >
                                <Icon icon="solar:upload-linear" className="text-xl" />
                                {uploading.length > 0 ? `Uploading ${uploading.length}…` : 'Click to upload'}
                                <input
                                    type="file"
                                    className="form-control w-auto mt-24 form-control-lg"
                                    id="file-upload-name"
                                    multiple
                                    hidden
                                    accept=".pdf,.doc,.docx,.txt"
                                    onChange={handleFileChange}
                                />
                            </label>
                        </div>
                    </div>
                </div>

                <div className="card-body px-16 py-12">
                    <div className="table-responsive scroll-sm">
                        <table className="table bordered-table sm-table mb-0">
                            <thead>
                                <tr>
                                    <th>ID</th>
                                    <th>Name</th>
                                    <th>Mimetype</th>
                                    <th>Size (bytes)</th>
                                    <th>Status</th>
                                    <th>Created At</th>
                                    <th>Download</th>
                                    <th>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* Shimmer while table is fetching */}
                                {loading ? (
                                    Array.from({ length: 5 }).map((_, idx) => (
                                        <tr key={`shimmer-row-${idx}`}>
                                            <td><div className="shimmer shimmer-id" /></td>
                                            <td><div className="shimmer shimmer-text" /></td>
                                            <td><div className="shimmer shimmer-text" /></td>
                                            <td><div className="shimmer shimmer-text" /></td>
                                            <td><div className="shimmer shimmer-badge" /></td>
                                            <td><div className="shimmer shimmer-text" /></td>
                                            <td><div className="shimmer shimmer-actions" /></td>
                                            <td><div className="shimmer shimmer-actions" /></td>
                                        </tr>
                                    ))
                                ) : (
                                    <>
                                        {/* NEW: Pending upload rows */}
                                        {uploading.map((u, i) => (
                                            <tr key={`uploading-${u.name}-${i}`}>
                                                <td>—</td>
                                                <td>{u.name}</td>
                                                <td>{u.mimetype}</td>
                                                <td>{u.bytes}</td>
                                                <td>
                                                    <span className={`badge text-sm fw-semibold px-20 py-9 radius-4 text-white ${getStatusStyle(u._failed ? 'failed' : 'uploading')}`}>
                                                        {u._failed ? 'Failed' : 'Uploading…'}
                                                    </span>
                                                </td>
                                                <td>—</td>
                                                <td>—</td>
                                                <td className="text-center">—</td>
                                            </tr>
                                        ))}

                                        {/* Existing files from server */}
                                        {files.length > 0 ? (
                                            files.map((file) => (
                                                <tr key={file?.id}>
                                                    <td>{file?.id}</td>
                                                    <td>{file?.name || "N/A"}</td>
                                                    <td>{file?.mimetype || "N/A"}</td>
                                                    <td>{file?.bytes ?? 0}</td>
                                                    <td>
                                                        <span className={`badge text-sm fw-semibold px-20 py-9 radius-4 text-white ${getStatusStyle(file?.status)}`}>
                                                            {file?.status || "N/A"}
                                                        </span>
                                                    </td>
                                                    <td>{file?.createdAt ? convertToIndianTimeZone(file.createdAt) : "N/A"}</td>
                                                    <td>
                                                        {file?.url ? (
                                                            <a href={file.url} target="_blank" rel="noopener noreferrer" className="btn btn-outline-primary btn-sm">
                                                                Download
                                                            </a>
                                                        ) : ("-")}
                                                    </td>
                                                    <td className="text-center">
                                                        <button
                                                            onClick={() => handleDeleteFile(file.id)}
                                                            className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                        >
                                                            <Icon icon="fluent:delete-24-regular" className="menu-icon" />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))
                                        ) : uploading.length === 0 ? (
                                            <tr>
                                                <td colSpan="8" className="text-center ">
                                                    <p className="text-secondary-light text-md mb-16">No files uploaded yet.</p>
                                                    <div>
                                                        <label
                                                            htmlFor="file-upload-name"
                                                            className="mb-0 border border-neutral-600 fw-medium text-secondary-light px-16 py-10 radius-12 d-inline-flex align-items-center gap-2 bg-hover-neutral-200"
                                                            style={{ fontSize: '14px', cursor: 'pointer' }}
                                                        >
                                                            <Icon icon="solar:upload-linear" className="text-xl" />
                                                            Click to upload
                                                            <input
                                                                type="file"
                                                                className="form-control w-auto mt-24 form-control-lg"
                                                                id="file-upload-name"
                                                                multiple
                                                                hidden
                                                                accept=".pdf,.doc,.docx,.txt"
                                                                onChange={handleFileChange}
                                                            />
                                                        </label>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : null}
                                    </>
                                )}
                            </tbody>
                        </table>
                    </div>

                    {/* Pagination */}
                    {files.length > 0 && !loading && (
                        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-10">
                            <div className="d-flex gap-2 ms-auto" style={{ justifyContent: 'flex-end' }}>
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => setPage((p) => Math.max(p - 1, 1))}
                                    disabled={loading || page === 1}
                                >
                                    Prev
                                </button>
                                <button
                                    className="btn btn-sm btn-outline-secondary"
                                    onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
                                    disabled={loading || page === totalPages}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}

                </div>
            </div>
        </div>
    );
}

export default KnowledgeBaseLayer;
